import { soundInstallWarnings } from "@/lib/compatibility"
import { fmtFileSize } from "@/lib/tls-format"
import type { Sound } from "@/lib/supabase-types"

interface Props {
  sound: Pick<Sound, "format" | "size">
  /** Layout hook for the detail column's mobile ordering; no styling of its own. */
  className?: string
  /**
   * The Download button hands over a clip cut to 5 s, which is always under
   * 1 MB, so the catalog file's size says nothing about what gets installed.
   */
  deliversTrimmedClip?: boolean
}

/**
 * Format and size problems that stop a file installing as a lock chime.
 *
 * Unconditional: these are properties of the SOUND, so every visitor sees
 * them. They used to hang off a vehicle picker, which meant the ~83 affected
 * sounds only warned the small slice of visitors who had filled in a form.
 *
 * PLACEMENT: directly under the title and description, above the waveform and
 * the download button, so it is read before the file is taken rather than
 * after. Not at the top of the page — the nav is position:fixed, so a leading
 * block there collides with it.
 *
 * DESIGN: factual metadata, not an error state. The design system has no
 * semantic red or amber, so this carries by ink weight and position alone —
 * verdict at --tls-fg-strong 700, explanation at --tls-fg-muted 600, no chip,
 * no card, no border box. It states the verdict and the fix rather than
 * repeating the spec sheet's neutral format/size rows.
 *
 * Renders nothing when the file installs as shipped, which is the
 * overwhelming majority of the catalog.
 */
export default function InstallWarnings({ sound, className, deliversTrimmedClip = false }: Props) {
  const warnings = soundInstallWarnings(sound).filter(
    w => !(deliversTrimmedClip && w.reason === "file_size_over_1mb")
  )
  if (warnings.length === 0) return null

  // One file-size format on the template (F-4): the verdict is authored in
  // lib/compatibility.ts with the raw catalog string ("1996.1 KB") while the
  // CTA and spec sheet print fmtFileSize ("1.9 MB"). Normalise the figure
  // here without forking the sentence; the copy stays single-sourced.
  const verdictFor = (w: (typeof warnings)[number]) => {
    if (w.reason !== "file_size_over_1mb" || !sound.size) return w.verdict
    const pretty = fmtFileSize(sound.size)
    return pretty ? w.verdict.replace(sound.size, pretty) : w.verdict
  }

  return (
    <section aria-label="Before you install" className={className} style={{ margin: "0 0 32px" }}>
      <p className="tls-eyebrow" style={{ color: "var(--tls-fg-subtle)" }}>
        before you install
      </p>
      {warnings.map(w => (
        <div key={w.reason} className="mt-2">
          <p className="text-sm font-bold text-[var(--tls-fg-strong)]">{verdictFor(w)}</p>
          <p className="mt-1 text-sm font-semibold leading-relaxed text-[var(--tls-fg-muted)]">
            {w.explanation}
          </p>
        </div>
      ))}
    </section>
  )
}
